const S3_BUCKET = "s3-zakupauto-service";
const REGION = "eu-central-1";

type OfferCardImageSource = {
    acf?: {
        card_image?: string | null;
        gallery?: any;
        filekeys?: string | null;
        gallery_order?: string[] | string | null;
    } | null;
};

const s3Url = (key: string) => `https://${S3_BUCKET}.s3.${REGION}.amazonaws.com/${key}`;

/**
 * List/card thumbnail URL.
 * Prefer slim API field `acf.card_image` (get_latest_cars / cars-by-author / list endpoints).
 * Fallback: first gallery image (large → url → medium) or first S3 filekey.
 */
export const getOfferCardImage = (car: OfferCardImageSource): string => {
    const acf = car.acf || {};
    const cardImage = typeof acf.card_image === "string" ? acf.card_image.trim() : "";
    if (cardImage) {
        return cardImage;
    }

    const wpGallery = Array.isArray(acf.gallery) ? acf.gallery : [];
    const fileKeys = String(acf.filekeys || "")
        .split("|")
        .filter(Boolean);
    const galleryOrder = Array.isArray(acf.gallery_order)
        ? acf.gallery_order
        : typeof acf.gallery_order === "string"
          ? acf.gallery_order.split("|").filter(Boolean)
          : [];
    const firstToken = galleryOrder[0];
    const orderedWpImage = firstToken?.startsWith("wp:")
        ? wpGallery.find((image: any) => String(image.id ?? image.ID) === firstToken.slice(3))
        : undefined;
    const orderedS3Key = firstToken?.startsWith("s3:") ? firstToken.slice(3) : undefined;
    const wpImage = orderedWpImage || wpGallery[0];
    const s3Key = orderedS3Key || fileKeys[0];

    return wpImage?.sizes?.large || wpImage?.url || wpImage?.sizes?.medium || (s3Key ? s3Url(s3Key) : "");
};

type SlimCardOfferInput = {
    id?: number | string;
    slug?: string | null;
    link?: string | null;
    title?: string | {rendered?: string} | null;
    content?: unknown;
    acf?: Record<string, any> | null;
};

const slugFromLink = (link?: string | null): string | undefined => {
    if (!link || typeof link !== "string") {
        return undefined;
    }
    try {
        const path = new URL(link).pathname.replace(/\/+$/, "");
        return path.split("/").filter(Boolean).pop() || undefined;
    } catch {
        return link.replace(/\/+$/, "").split("/").filter(Boolean).pop() || undefined;
    }
};

/**
 * Drop heavy gallery/content before putting list offers into Redux/SSR.
 * Keeps only fields needed by offer cards (+ light seller location bits).
 */
export const slimOfferForCardList = <T extends SlimCardOfferInput>(raw: T): T => {
    const acf = raw.acf && typeof raw.acf === "object" ? raw.acf : {};
    const id = Number(raw.id);
    const slug =
        (typeof raw.slug === "string" && raw.slug) || slugFromLink(raw.link) || (Number.isFinite(id) ? String(id) : "");
    const title = typeof raw.title === "string" ? raw.title : raw.title?.rendered || "";
    const card_image = getOfferCardImage({acf});

    return {
        id,
        slug,
        title,
        content: "",
        acf: {
            make: acf.make,
            model: acf.model,
            year: acf.year,
            millage: acf.millage,
            engine: acf.engine,
            price: acf.price,
            gearbox: acf.gearbox,
            card_image: card_image || acf.card_image || "",
            filekeys: "",
            sellers_name: acf.sellers_name,
            city: acf.city,
            wojew: acf.wojew,
            body_type: acf.body_type,
            horse_power: acf.horse_power,
            engine_size: acf.engine_size
        }
    } as T;
};
